import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Launch as LaunchIcon,
  Link as LinkIcon,
  Publish as PublishIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon
} from '@mui/icons-material';
import api from '../../services/api';

const WEBSITE_ROLE = 'website-download';
const RUNTIME_ROLE = 'launcher-runtime';

const createUploadForm = (artifactRole = RUNTIME_ROLE) => ({
  artifactRole,
  platform: artifactRole === RUNTIME_ROLE ? 'windows' : '',
  version: '',
  buildNumber: '',
  description: '',
  systemRequirements: '',
  releaseNotes: '',
  executableRelativePath: artifactRole === RUNTIME_ROLE ? 'Virtual Medic.exe' : '',
  cloudObjectPath: '',
  externalDownloadUrl: '',
  file: null
});

const createEditRuntimeForm = () => ({
  id: '',
  version: '',
  buildNumber: '',
  description: '',
  releaseNotes: '',
  executableRelativePath: ''
});

const getPlatformColor = (platform) => {
  const colors = {
    windows: '#0078D4',
    macos: '#111111',
    ios: '#000000',
    android: '#3DDC84'
  };
  return colors[platform] || '#5f6b7a';
};

const getRuntimeStatusColor = (runtimeStatus, runtimeIsActive) => {
  if (runtimeIsActive) {
    return 'success';
  }
  switch (runtimeStatus) {
    case 'published':
      return 'info';
    case 'archived':
      return 'default';
    default:
      return 'warning';
  }
};

const formatRoleLabel = (artifactRole) => (
  artifactRole === RUNTIME_ROLE ? 'Launcher Runtime' : 'Website Download'
);

const formatSourceLabel = (source) => {
  switch (source) {
    case 'unity-cloud-build':
      return 'Unity Cloud Build';
    case 'manual-link':
      return 'External Link';
    case 'migration':
      return 'Migration';
    default:
      return 'Admin Upload';
  }
};

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes)) return '-';
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const defaultExecutablePathForPlatform = (platform) => (
  platform === 'macos' ? 'Virtual Medic.app' : 'Virtual Medic.exe'
);

function BuildManagement() {
  const [activeTab, setActiveTab] = useState(RUNTIME_ROLE);
  const [websiteBuilds, setWebsiteBuilds] = useState([]);
  const [runtimeBuilds, setRuntimeBuilds] = useState([]);
  const [runtimeManifestUrl, setRuntimeManifestUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkForm, setLinkForm] = useState({ id: '', platform: '', externalDownloadUrl: '' });
  const [editRuntimeDialogOpen, setEditRuntimeDialogOpen] = useState(false);
  const [editRuntimeSaving, setEditRuntimeSaving] = useState(false);
  const [editRuntimeForm, setEditRuntimeForm] = useState(createEditRuntimeForm());
  const [busyRuntimeId, setBusyRuntimeId] = useState('');
  const [busyWebsiteId, setBusyWebsiteId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadForm, setUploadForm] = useState(createUploadForm(RUNTIME_ROLE));

  const currentUploadRole = uploadForm.artifactRole;

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchBuildData = useCallback(async () => {
    try {
      const [websiteResponse, runtimeResponse] = await Promise.all([
        api.get('/admin/builds', { params: { artifactRole: WEBSITE_ROLE } }),
        api.get('/admin/launcher-runtime-builds')
      ]);

      setWebsiteBuilds(websiteResponse.data || []);
      setRuntimeBuilds(runtimeResponse.data?.builds || []);
      setRuntimeManifestUrl(runtimeResponse.data?.publicManifestUrl || '');
    } catch (error) {
      showSnackbar('Failed to fetch build data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBuildData();
  }, [fetchBuildData]);

  const resetUploadForm = useCallback((role = activeTab) => {
    setUploadForm(createUploadForm(role));
  }, [activeTab]);

  const openUploadDialog = (role = activeTab) => {
    setUploadForm(createUploadForm(role));
    setUploadDialogOpen(true);
  };

  const handleUploadRoleChange = (role) => {
    setUploadForm(createUploadForm(role));
  };

  const handleUploadPlatformChange = (platform) => {
    setUploadForm((prev) => ({
      ...prev,
      platform,
      executableRelativePath: prev.artifactRole === RUNTIME_ROLE
        ? defaultExecutablePathForPlatform(platform)
        : prev.executableRelativePath
    }));
  };

  const handleUploadSubmit = async (event) => {
    event.preventDefault();

    const trimmedExternalUrl = uploadForm.externalDownloadUrl.trim();
    const hasFile = Boolean(uploadForm.file);
    const hasExternalLink = trimmedExternalUrl.length > 0;

    if (!uploadForm.platform || !uploadForm.version || (!hasFile && !hasExternalLink)) {
      showSnackbar('Please fill in the required build fields', 'error');
      return;
    }

    if (uploadForm.artifactRole === RUNTIME_ROLE && !hasFile) {
      showSnackbar('Launcher runtime builds must upload a zip file', 'error');
      return;
    }

    setUploadLoading(true);

    const formData = new FormData();
    if (uploadForm.file) {
      formData.append('buildFile', uploadForm.file);
    }
    formData.append('artifactRole', uploadForm.artifactRole);
    formData.append('platform', uploadForm.platform);
    formData.append('version', uploadForm.version);
    formData.append('buildNumber', uploadForm.buildNumber.trim());
    formData.append('description', uploadForm.description);
    formData.append('systemRequirements', uploadForm.systemRequirements);
    formData.append('releaseNotes', uploadForm.releaseNotes);
    formData.append('executableRelativePath', uploadForm.executableRelativePath.trim());
    formData.append('cloudObjectPath', uploadForm.cloudObjectPath.trim());
    formData.append('externalDownloadUrl', trimmedExternalUrl);

    try {
      await api.post('/admin/builds', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      showSnackbar(
        uploadForm.artifactRole === RUNTIME_ROLE
          ? 'Runtime build uploaded as draft'
          : 'Website build uploaded successfully'
      );
      setUploadDialogOpen(false);
      resetUploadForm(uploadForm.artifactRole);
      fetchBuildData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to upload build', 'error');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownload = async (build) => {
    try {
      if (build.publicDownloadUrl && build.artifactRole === RUNTIME_ROLE && build.runtimeIsActive) {
        window.open(build.publicDownloadUrl, '_blank', 'noopener');
        return;
      }

      if (build.externalDownloadUrl && build.artifactRole !== RUNTIME_ROLE) {
        window.open(build.externalDownloadUrl, '_blank', 'noopener');
        return;
      }

      const response = await api.get(`/admin/builds/${build._id}/download-url`);
      window.open(response.data.downloadUrl, '_blank', 'noopener');
    } catch (error) {
      showSnackbar('Failed to prepare download', 'error');
    }
  };

  const handlePublishRuntime = async (build) => {
    setBusyRuntimeId(build._id);
    try {
      await api.post(`/admin/launcher-runtime-builds/${build._id}/publish`);
      showSnackbar(
        `Published ${build.platform} runtime ${build.version}${build.buildNumber ? ` (${build.buildNumber})` : ''}`
      );
      fetchBuildData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to publish runtime build', 'error');
    } finally {
      setBusyRuntimeId('');
    }
  };

  const handleDeleteRuntime = async (build) => {
    if (!window.confirm(`Delete runtime build ${build.version}${build.buildNumber ? ` (${build.buildNumber})` : ''}?`)) {
      return;
    }

    setBusyRuntimeId(build._id);
    try {
      await api.delete(`/admin/launcher-runtime-builds/${build._id}`);
      showSnackbar('Runtime build deleted');
      fetchBuildData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to delete runtime build', 'error');
    } finally {
      setBusyRuntimeId('');
    }
  };

  const openEditRuntimeDialog = (build) => {
    setEditRuntimeForm({
      id: build._id,
      version: build.version || '',
      buildNumber: build.buildNumber || '',
      description: build.description || '',
      releaseNotes: build.releaseNotes || '',
      executableRelativePath: build.executableRelativePath || defaultExecutablePathForPlatform(build.platform)
    });
    setEditRuntimeDialogOpen(true);
  };

  const handleSaveRuntimeEdit = async () => {
    if (!editRuntimeForm.id) {
      return;
    }

    setEditRuntimeSaving(true);
    try {
      await api.patch(`/admin/launcher-runtime-builds/${editRuntimeForm.id}`, {
        version: editRuntimeForm.version,
        buildNumber: editRuntimeForm.buildNumber,
        description: editRuntimeForm.description,
        releaseNotes: editRuntimeForm.releaseNotes,
        executableRelativePath: editRuntimeForm.executableRelativePath
      });
      showSnackbar('Runtime build details updated');
      setEditRuntimeDialogOpen(false);
      fetchBuildData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to update runtime build', 'error');
    } finally {
      setEditRuntimeSaving(false);
    }
  };

  const handleToggleWebsiteStatus = async (buildId) => {
    setBusyWebsiteId(buildId);
    try {
      await api.patch(`/admin/builds/${buildId}/toggle`);
      showSnackbar('Website build status updated');
      fetchBuildData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to update build status', 'error');
    } finally {
      setBusyWebsiteId('');
    }
  };

  const handleDeleteWebsite = async (buildId) => {
    if (!window.confirm('Delete this website download build?')) {
      return;
    }

    setBusyWebsiteId(buildId);
    try {
      await api.delete(`/admin/builds/${buildId}`);
      showSnackbar('Website build deleted');
      fetchBuildData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to delete website build', 'error');
    } finally {
      setBusyWebsiteId('');
    }
  };

  const openWebsiteLinkDialog = (build) => {
    setLinkForm({
      id: build._id,
      platform: build.platform,
      externalDownloadUrl: build.externalDownloadUrl || ''
    });
    setLinkDialogOpen(true);
  };

  const handleWebsiteLinkSave = async () => {
    if (!linkForm.id) {
      return;
    }

    setLinkSaving(true);
    try {
      await api.patch(`/admin/builds/${linkForm.id}/external-link`, {
        externalDownloadUrl: linkForm.externalDownloadUrl
      });
      showSnackbar('Website download link updated');
      setLinkDialogOpen(false);
      fetchBuildData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to update website download link', 'error');
    } finally {
      setLinkSaving(false);
    }
  };

  const runtimeSummary = useMemo(() => ({
    total: runtimeBuilds.length,
    draft: runtimeBuilds.filter((build) => build.runtimeStatus === 'draft').length,
    published: runtimeBuilds.filter((build) => build.runtimeIsActive).length
  }), [runtimeBuilds]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5">Build Management</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Review draft runtime artifacts, publish launcher releases when ready, and manage website download links separately.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => openUploadDialog(activeTab)}
          sx={{ bgcolor: '#16a085', '&:hover': { bgcolor: '#1abc9c' } }}
        >
          {activeTab === RUNTIME_ROLE ? 'Upload Runtime Build' : 'Upload Website Build'}
        </Button>
      </Box>

      <Tabs value={activeTab} onChange={(_event, nextValue) => setActiveTab(nextValue)} sx={{ mb: 3 }}>
        <Tab value={RUNTIME_ROLE} label="Launcher Runtime Releases" />
        <Tab value={WEBSITE_ROLE} label="Website Downloads" />
      </Tabs>

      {activeTab === RUNTIME_ROLE && (
        <Stack spacing={3}>
          <Alert severity="info">
            Unity Cloud Build uploads should land here as draft zip artifacts. Nothing goes live to the launcher until you click <strong>Publish / Set Active</strong>.
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="overline" color="text.secondary">Runtime Builds</Typography>
                <Typography variant="h4">{runtimeSummary.total}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="overline" color="text.secondary">Draft Builds</Typography>
                <Typography variant="h4">{runtimeSummary.draft}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="overline" color="text.secondary">Live Platforms</Typography>
                <Typography variant="h4">{runtimeSummary.published}</Typography>
              </Paper>
            </Grid>
          </Grid>

          {runtimeManifestUrl && (
            <Alert
              severity="success"
              action={(
                <Button color="inherit" size="small" onClick={() => window.open(runtimeManifestUrl, '_blank', 'noopener')}>
                  Open Manifest
                </Button>
              )}
            >
              Public launcher manifest: {runtimeManifestUrl}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f7f9fc' }}>
                    <TableCell>Platform</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Build #</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Executable</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runtimeBuilds.map((build) => (
                    <TableRow key={build._id} hover>
                      <TableCell>
                        <Chip label={build.platform.toUpperCase()} sx={{ bgcolor: getPlatformColor(build.platform), color: 'white', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell>{build.version}</TableCell>
                      <TableCell>{build.buildNumber || '-'}</TableCell>
                      <TableCell>
                        <Chip label={formatSourceLabel(build.source)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip
                            label={build.runtimeIsActive ? 'Active Live Build' : build.runtimeStatus}
                            color={getRuntimeStatusColor(build.runtimeStatus, build.runtimeIsActive)}
                            size="small"
                          />
                          {build.publicDownloadUrl && (
                            <Chip label="Public Zip Ready" size="small" variant="outlined" color="success" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{build.executableRelativePath || '-'}</TableCell>
                      <TableCell>{formatDateTime(build.createdAt)}</TableCell>
                      <TableCell>{build.originalFilename || build.filename || '-'}</TableCell>
                      <TableCell>{formatFileSize(build.fileSize)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Download build zip">
                            <span>
                              <IconButton size="small" onClick={() => handleDownload(build)}>
                                <DownloadIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {build.publicDownloadUrl && (
                            <Tooltip title="Open public zip URL">
                              <span>
                                <IconButton size="small" onClick={() => window.open(build.publicDownloadUrl, '_blank', 'noopener')}>
                                  <LaunchIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit build details">
                            <span>
                              <IconButton size="small" onClick={() => openEditRuntimeDialog(build)}>
                                <EditIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={build.runtimeIsActive ? 'Already live' : 'Publish / Set active'}>
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                disabled={busyRuntimeId === build._id || build.runtimeIsActive}
                                onClick={() => handlePublishRuntime(build)}
                              >
                                {busyRuntimeId === build._id ? <CircularProgress size={18} /> : <PublishIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete build">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={busyRuntimeId === build._id}
                                onClick={() => handleDeleteRuntime(build)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {runtimeBuilds.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                        <Typography variant="h6" color="text.secondary">No launcher runtime builds yet</Typography>
                        <Typography variant="body2" color="text.secondary">Unity Cloud Build uploads will appear here as draft runtime artifacts.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      )}

      {activeTab === WEBSITE_ROLE && (
        <Stack spacing={3}>
          <Alert severity="info">
            These are the public website download cards. Activating a build here affects the public downloads page, not the launcher runtime manifest.
          </Alert>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f7f9fc' }}>
                    <TableCell>Platform</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Filename</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell>Download Link</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {websiteBuilds.map((build) => (
                    <TableRow key={build._id} hover>
                      <TableCell>
                        <Chip label={build.platform.toUpperCase()} sx={{ bgcolor: getPlatformColor(build.platform), color: 'white', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={formatRoleLabel(build.artifactRole || WEBSITE_ROLE)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{build.version}</TableCell>
                      <TableCell>{build.originalFilename || (build.externalDownloadUrl ? 'External link' : '-')}</TableCell>
                      <TableCell>{formatFileSize(build.fileSize)}</TableCell>
                      <TableCell>{formatDateTime(build.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={build.externalDownloadUrl ? 'Linked' : 'Managed'}
                          color={build.externalDownloadUrl ? 'success' : 'default'}
                          icon={<LinkIcon />}
                          size="small"
                          variant={build.externalDownloadUrl ? 'filled' : 'outlined'}
                          onClick={() => openWebsiteLinkDialog(build)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={build.isActive ? 'Active' : 'Inactive'} color={build.isActive ? 'success' : 'default'} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Download build">
                            <span>
                              <IconButton size="small" onClick={() => handleDownload(build)}>
                                <DownloadIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={build.isActive ? 'Deactivate' : 'Activate'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={busyWebsiteId === build._id}
                                onClick={() => handleToggleWebsiteStatus(build._id)}
                              >
                                {build.isActive ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete build">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={busyWebsiteId === build._id}
                                onClick={() => handleDeleteWebsite(build._id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {websiteBuilds.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                        <Typography variant="h6" color="text.secondary">No website download builds yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      )}

      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={handleUploadSubmit}>
          <DialogTitle>{currentUploadRole === RUNTIME_ROLE ? 'Upload Launcher Runtime Build' : 'Upload Website Download Build'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Build Role</InputLabel>
                  <Select
                    value={uploadForm.artifactRole}
                    label="Build Role"
                    onChange={(event) => handleUploadRoleChange(event.target.value)}
                  >
                    <MenuItem value={RUNTIME_ROLE}>Launcher Runtime</MenuItem>
                    <MenuItem value={WEBSITE_ROLE}>Website Download</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={uploadForm.platform}
                    label="Platform"
                    onChange={(event) => handleUploadPlatformChange(event.target.value)}
                  >
                    <MenuItem value="windows">Windows</MenuItem>
                    <MenuItem value="macos">macOS</MenuItem>
                    {currentUploadRole === WEBSITE_ROLE && <MenuItem value="ios">iOS</MenuItem>}
                    {currentUploadRole === WEBSITE_ROLE && <MenuItem value="android">Android</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Version"
                  value={uploadForm.version}
                  onChange={(event) => setUploadForm((prev) => ({ ...prev, version: event.target.value }))}
                  placeholder="e.g. 0.2.0"
                />
              </Grid>
              {currentUploadRole === RUNTIME_ROLE && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Build Number"
                    value={uploadForm.buildNumber}
                    onChange={(event) => setUploadForm((prev) => ({ ...prev, buildNumber: event.target.value }))}
                    placeholder="e.g. 142"
                    helperText="Use the Unity Cloud Build number or another monotonic build counter."
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Button component="label" variant="outlined" startIcon={<UploadIcon />} sx={{ mb: 2 }}>
                  Choose Build File
                  <input
                    type="file"
                    hidden
                    accept={currentUploadRole === RUNTIME_ROLE ? '.zip' : '.exe,.dmg,.ipa,.apk,.msi,.deb,.rpm,.zip,.pkg'}
                    onChange={(event) => setUploadForm((prev) => ({ ...prev, file: event.target.files?.[0] || null }))}
                  />
                </Button>
                {uploadForm.file && (
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Selected: {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
                  </Typography>
                )}
                <FormHelperText>
                  {currentUploadRole === RUNTIME_ROLE
                    ? 'Upload the exact runtime zip artifact. For macOS, create the zip on a Mac so app permissions stay intact.'
                    : 'Upload a file or provide an external download link. Managed uploads can still be stored in cloud storage automatically.'}
                </FormHelperText>
              </Grid>
              {currentUploadRole === RUNTIME_ROLE && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Executable Relative Path"
                    value={uploadForm.executableRelativePath}
                    onChange={(event) => setUploadForm((prev) => ({ ...prev, executableRelativePath: event.target.value }))}
                    helperText="The launcher uses this path after extracting the runtime zip. Example: Virtual Medic.exe or Virtual Medic.app"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={uploadForm.description}
                  onChange={(event) => setUploadForm((prev) => ({ ...prev, description: event.target.value }))}
                />
              </Grid>
              {currentUploadRole === WEBSITE_ROLE && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="System Requirements"
                    multiline
                    rows={2}
                    value={uploadForm.systemRequirements}
                    onChange={(event) => setUploadForm((prev) => ({ ...prev, systemRequirements: event.target.value }))}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Release Notes"
                  multiline
                  rows={3}
                  value={uploadForm.releaseNotes}
                  onChange={(event) => setUploadForm((prev) => ({ ...prev, releaseNotes: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Cloud Object Path Override"
                  value={uploadForm.cloudObjectPath}
                  onChange={(event) => setUploadForm((prev) => ({ ...prev, cloudObjectPath: event.target.value }))}
                  placeholder={currentUploadRole === RUNTIME_ROLE
                    ? 'downloads/builds/launcher-runtime/windows/0.2.0/b142/virtual-medic-windows-v0.2.0-b142.zip'
                    : 'downloads/builds/windows/0.2.0/virtual-medic-installer.zip'}
                  helperText="Optional. Leave blank to let the server choose the object path automatically."
                />
              </Grid>
              {currentUploadRole === WEBSITE_ROLE && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="External Download Link"
                    value={uploadForm.externalDownloadUrl}
                    onChange={(event) => setUploadForm((prev) => ({ ...prev, externalDownloadUrl: event.target.value }))}
                    placeholder="https://example.com/download"
                    helperText="Optional. Use this only when the file lives somewhere else and you want the website to redirect to it."
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={uploadLoading} sx={{ bgcolor: '#16a085', '&:hover': { bgcolor: '#1abc9c' } }}>
              {uploadLoading ? <CircularProgress size={22} /> : 'Upload Build'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={editRuntimeDialogOpen} onClose={() => setEditRuntimeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Launcher Runtime Build</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Version"
                value={editRuntimeForm.version}
                onChange={(event) => setEditRuntimeForm((prev) => ({ ...prev, version: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Build Number"
                value={editRuntimeForm.buildNumber}
                onChange={(event) => setEditRuntimeForm((prev) => ({ ...prev, buildNumber: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Executable Relative Path"
                value={editRuntimeForm.executableRelativePath}
                onChange={(event) => setEditRuntimeForm((prev) => ({ ...prev, executableRelativePath: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={editRuntimeForm.description}
                onChange={(event) => setEditRuntimeForm((prev) => ({ ...prev, description: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Release Notes"
                multiline
                rows={4}
                value={editRuntimeForm.releaseNotes}
                onChange={(event) => setEditRuntimeForm((prev) => ({ ...prev, releaseNotes: event.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRuntimeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRuntimeEdit} disabled={editRuntimeSaving} sx={{ bgcolor: '#16a085', '&:hover': { bgcolor: '#1abc9c' } }}>
            {editRuntimeSaving ? <CircularProgress size={22} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set website download link</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="External download link"
            value={linkForm.externalDownloadUrl}
            onChange={(event) => setLinkForm((prev) => ({ ...prev, externalDownloadUrl: event.target.value }))}
            placeholder="https://example.com/download"
            helperText="Leave blank to clear the link and fall back to the uploaded artifact."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleWebsiteLinkSave} disabled={linkSaving} sx={{ bgcolor: '#16a085', '&:hover': { bgcolor: '#1abc9c' } }}>
            {linkSaving ? <CircularProgress size={22} /> : 'Save Link'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default BuildManagement;
